import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/api";

export function BuyOrQuote({ product, onQuote, size = "md" }) {
  const { add } = useCart();
  const h = size === "lg" ? "h-14 px-7" : "h-12 px-5";
  if (product?.price && product?.available) {
    return (
      <button data-testid={`add-to-cart-${product.slug}`} onClick={() => add(product)} className={`btn-volt ${h} font-mono text-xs font-bold uppercase tracking-widest`}>
        <span className="flex items-center gap-2"><ShoppingBag className="h-4 w-4" /> Add to Cart</span>
      </button>
    );
  }
  return (
    <button data-testid={`request-quote-${product?.slug}`} onClick={() => onQuote?.(product)} className={`${h} border border-[var(--rescue)] text-white font-mono text-xs font-bold uppercase tracking-widest transition-colors hover:bg-[var(--rescue)]`}>
      <span className="flex items-center gap-2"><FileText className="h-4 w-4" /> Request Quote</span>
    </button>
  );
}

export function PriceTag({ product, className = "" }) {
  return (
    <p data-testid={`price-${product?.slug}`} className={`font-mono text-sm uppercase tracking-widest ${className}`}>
      {product?.price ? <span className="font-display text-4xl font-black tracking-normal text-volt" style={{ fontWeight: 900 }}>{formatPrice(product.price)}</span> : <span className="text-zinc-300">Price on request</span>}
    </p>
  );
}

export function PurchaseBox({ product, products = [], onQuote, size = "md", extra = null, priceClass = "mt-6" }) {
  const variants = products.filter((v) => v.parent === product.slug).sort((a, b) => a.order - b.order);
  const [sel, setSel] = useState(null);
  const active = variants.find((v) => v.slug === sel) || variants[0] || product;
  return (
    <div data-testid={`purchase-box-${product.slug}`}>
      {variants.length > 0 && (
        <div className={`${priceClass} flex flex-wrap gap-2`}>
          {variants.map((v) => (
            <button
              key={v.slug}
              data-testid={`variant-${v.slug}`}
              onClick={() => setSel(v.slug)}
              className={`h-11 px-5 font-mono text-xs font-bold uppercase tracking-widest border transition-colors ${active.slug === v.slug ? "border-[var(--volt)] bg-volt text-black" : "border-white/20 text-zinc-200 hover:border-[var(--volt)]"}`}
            >
              {v.variant_label}
            </button>
          ))}
        </div>
      )}
      <PriceTag product={active} className={variants.length ? "mt-4" : priceClass} />
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <BuyOrQuote product={active} onQuote={onQuote} size={size} />
        {extra}
      </div>
    </div>
  );
}

export function Highlights({ product, className = "" }) {
  const items = Array.isArray(product?.highlights) ? product.highlights : [];
  if (!items.length) return null;
  return (
    <div data-testid={`highlights-${product.slug}`} className={`grid gap-3 sm:grid-cols-2 ${className}`}>
      {items.map(([title, text], i) => (
        <div key={title} data-testid={`highlight-${product.slug}-${i}`} className="relative border border-[rgba(212,230,10,0.45)] bg-[rgba(212,230,10,0.06)] p-4">
          <span className="absolute left-0 top-0 h-full w-[3px] bg-volt" />
          <p className="font-display text-2xl font-black uppercase leading-none text-volt">{title}</p>
          <p className="mt-2 text-sm text-zinc-200">{text}</p>
        </div>
      ))}
    </div>
  );
}

export function DetailsLink({ slug }) {
  return (
    <Link to={`/shop/${slug}`} data-testid={`details-link-${slug}`} className="group inline-flex h-12 items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-300 hover:text-white">
      Full Specs <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </Link>
  );
}
